<template>
  <Listbox
    v-slot="{ open }"
    v-model="selectedItem"
    as="div"
    :multiple="multiple"
    :disabled="disabled"
  >
    <ListboxLabel v-if="label" class="inline-block mb-2">
      <slot name="label">
        <span
          class="ui-text-sm text-primary op"
          :class="{ 'opacity-50': disabled }"
        >
          {{ label }}
        </span>
      </slot>
    </ListboxLabel>
    <Float
      as="div"
      placement="bottom-start"
      :floating-as="floatingAs"
      class="relative"
      flip
      adaptive-width
      :offset="4"
    >
      <ListboxButton as="template">
        <slot name="activator" :selected-item="selectedItem" :open="open">
          <button
            :class="[
              errorMessage ? '!border-red' : 'border-primary',
              disabled ? 'opacity-50' : '',
            ]"
            class="h-11 ui-text-sm relative flex w-full items-center border bg-white py-1.5 px-3 text-left font-normal text-primary focus:outline-none"
          >
            <span v-if="hasValues && selectedItem" class="flex-auto truncate">
              {{
                Array.isArray(selectedItem)
                  ? selectedItem.map((item) => item?.[titleKey]).join(", ")
                  : selectedItem[titleKey]
              }}
            </span>
            <span v-else class="flex-auto select-none truncate text-light-4">
              {{ placeholder }}
            </span>

            <UiIcon
              :name="open ? 'expand_less' : 'expand_more'"
              class="pointer-events-none h-5 w-5 flex-shrink-0 text-primary"
            />
          </button>
        </slot>
      </ListboxButton>

      <ListboxOptions
        class="max-h-60w-full transform-gpu overflow-auto bg-white border border-gray-600 space-y-3 p-3 focus:outline-none md:min-w-min ui-text-sm"
        :class="dropdownClasses"
      >
        <ListboxOption
          v-for="item in items"
          v-slot="{ active, selected }"
          :key="item.value"
          :value="item"
          as="template"
        >
          <li
            class="relative cursor-pointer select-none text-primary"
            :class="[itemClasses]"
          >
            <span class="block truncate font-normal">
              <slot
                name="item"
                :item="item"
                :active="active"
                :selected="selected"
              >
                {{ item[titleKey] }}
              </slot>
            </span>
          </li>
        </ListboxOption>
      </ListboxOptions>
    </Float>
    <FieldHint
      v-if="!hideDetails"
      :error="Boolean(errorMessage)"
      :message="errorMessage || hint"
    />
  </Listbox>
</template>

<script setup lang="ts" generic="T extends { [key: string]: any }">
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  provideUseId,
  ListboxLabel,
} from "@headlessui/vue";
import { Float } from "@headlessui-float/vue";
import { useField } from "vee-validate";
import FieldHint from "~/components/shared/FieldHint.vue";

const props = withDefaults(
  defineProps<{
    modelValue: any;
    name: string;
    items: T[];
    placeholder?: string;
    hint?: string;
    label?: string;
    hideDetails?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    titleKey?: string;
    valueKey?: string;
    updateInitValue?: boolean;
    big?: boolean;
    itemClasses?: string;
    floatingAs?: string;
    dropdownClasses?: string;
  }>(),
  {
    placeholder: "",
    hint: "",
    label: "",
    titleKey: "label",
    valueKey: "value",
    floatingAs: "div",
    itemClasses: "",
    dropdownClasses: "",
  },
);

provideUseId(() => useId());
// provideUseId(() => props.name);

const emit = defineEmits<{
  (
    e: "update:modelValue",
    selectedValue: Nullable<T> | undefined | (Nullable<T> | undefined)[],
  ): void;
}>();

const { errorMessage, handleChange, meta } = useField(props.name, undefined, {
  initialValue: props.modelValue,
});

const selectedItem = computed({
  get() {
    if (isArray(props.modelValue)) {
      return props.modelValue.map((value) => {
        return findCurrentSelection(value);
      });
    } else {
      return findCurrentSelection(props.modelValue);
    }
  },
  set(newValue) {
    if (props.name) {
      // handleChange(props.modelValue);
      handleChange(newValue);
    }

    emit("update:modelValue", newValue);
  },
});

onMounted(() => {
  if (props.modelValue) {
    if (props.name) {
      handleChange(findCurrentSelection(props.modelValue));
    }

    // emit("update:modelValue", findCurrentSelection(props.modelValue));
  }
});

function findCurrentSelection(value: Nullable<T | ValueOf<keyof T>>) {
  if (!value) return null;
  if (typeof value !== "object") {
    return props.items.find((item) => {
      return item[props.valueKey] === value;
    });
  } else {
    return props.items.find((item) => {
      return item[props.valueKey] === value[props.valueKey];
    });
  }
}
function setCurrentSelection() {
  if (Array.isArray(props.modelValue)) {
    const currentModelValue = props.modelValue.map((value) => {
      return findCurrentSelection(value);
    });
    emit("update:modelValue", currentModelValue);
    return currentModelValue;
  } else {
    const currentModelValue = findCurrentSelection(props.modelValue);

    emit("update:modelValue", currentModelValue);
    return currentModelValue;
  }
}

if (props.updateInitValue) {
  setCurrentSelection();
}

const hasValues = computed(() => {
  return Boolean(
    isArray(selectedItem.value)
      ? selectedItem.value.length
      : selectedItem.value,
  );
});

defineExpose({
  meta,
  value: props.modelValue,
  name: props.name,
});
</script>
